import React, {
	useRef,
	useEffect,
	useMemo,
	useState,
	useCallback
} from 'react';
import Slider from 'react-slick';
import PropTypes from 'prop-types';
import { Image } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useRequestData } from 'wsm-common-data';

import { REACT_SLICK_SPEED } from '../../global-constants';

import { galleryTrackingEvents } from '../../utilities/tracking';
import { centerThumbnailInView } from '../../utilities/centerSelectedThumbnail';
import {
	createSwipeDetector,
	getMatrixTranslateX
} from '../../utilities/createSwipeDetector';

import './thumbnails.scss';

const SLIDE_TO_SHOW = 6;
const ANIMATION_SPEED = REACT_SLICK_SPEED + 50; // Larger than react-slick's speed
const TOUCH_RESET_DELAY = 200; // Delay before resetting touch state after touchend

function Thumbnails({
	carouselIndex,
	isMobile,
	setCarouselIndex,
	modalSliderRef,
	imgTitle,
	isHorizontalLayout = false
}) {
	const { imagesToDisplay = [] } = useSelector((state) => state.media);
	const { windowId, widgetName } = useRequestData();
	const isInitialMount = useRef(true);
	const containerRef = useRef(null);
	const slider = useRef();
	const [slidesToShow, setSlidesToShow] = useState(null);
	const imagesCount = imagesToDisplay.length;
	const [isAddedTabIndex, setIsAddedTabIndex] = useState(false);
	const animationEndCallback = useRef(null); // Animation lock like react-slick
	const isTouching = useRef(false); // Distinguish touch drag from VoiceOver focus
	const isReinitSlider = useRef(true);
	const touchTimerRef = useRef(null); // Timer ref for handleTouchEnd cleanup

	const swipeDetector = useMemo(() => createSwipeDetector(), []);

	// Support small swipe in mobile
	const [thumbnailActivePos, setThumbnailActivePos] = useState(0);
	const [rightTranslateX, setRightTranslateX] = useState(0);

	const [touchMove, setTouchMove] = useState(0);

	const touchStartX = useRef(0);

	// Helper to get slickTrack element - avoids repeated DOM queries
	const getSlickTrack = () =>
		containerRef.current?.querySelector('.slick-track');

	// Track touch gestures so onFocusSlide can distinguish
	// real touch (drag/tap) from VoiceOver focus navigation.
	// VoiceOver doesn't fire touch events, so isTouching stays false.

	const handleTouchStart = (event) => {
		isTouching.current = true;
		swipeDetector.onTouchStart(event);
		touchStartX.current = event.touches[0].clientX;
	};

	const touchEndRef = useRef(null);
	const handleTouchEnd = (event) => {
		swipeDetector.onTouchEnd(event);
		// Store timer ID in ref so it can be cleared on unmount or re-trigger
		clearTimeout(touchTimerRef.current);
		touchTimerRef.current = setTimeout(() => {
			isTouching.current = false;
			setTouchMove(0);
			// Only trigger swipe logic when user actually swiped (not tapped)
		}, TOUCH_RESET_DELAY);

		if (isMobile) {
			// Support swipe move in mobile
			const slickTrack = getSlickTrack();
			if (!slickTrack) return;
			const computedTransform =
				window.getComputedStyle(slickTrack).transform;
			const currentTransformX = getMatrixTranslateX(computedTransform);

			if (currentTransformX > 0) {
				const transform = `translate3d(${0}px, 0, 0)`;
				touchEndRef.current = setTimeout(() => {
					slickTrack.style.setProperty(
						'transform',
						transform,
						'important'
					);
					setThumbnailActivePos(0);
				}, TOUCH_RESET_DELAY);
			} else if (currentTransformX < rightTranslateX) {
				const transform = `translate3d(${rightTranslateX}px, 0, 0)`;
				touchEndRef.current = setTimeout(() => {
					slickTrack.style.setProperty(
						'transform',
						transform,
						'important'
					);
					// setThumbnailActivePos(0);
					setThumbnailActivePos(rightTranslateX);
				}, REACT_SLICK_SPEED);
			} else {
				setThumbnailActivePos(thumbnailActivePos + touchMove);
			}
		}
	};
	const handleTouchMove = (event) => {
		const currentX = event.touches[0].clientX;
		const deltaX = currentX - touchStartX.current;
		setTouchMove(deltaX);
		clearTimeout(touchEndRef.current);
		touchEndRef.current = null;
	};

	// Cleanup timer refs on unmount to prevent state updates on unmounted component
	useEffect(() => {
		return () => {
			clearTimeout(touchTimerRef.current);
			clearTimeout(touchEndRef.current);
		};
	}, []);
	const focusTriggeredNav = useRef(false); // Skip thumbnail animation when VoiceOver focus triggers nav

	const settings = useMemo(
		() => ({
			infinite: false,
			slidesToScroll: 1,
			vertical: !isMobile && !isHorizontalLayout,
			lazyLoad: false,
			focusOnSelect: false,
			variableWidth: isMobile || isHorizontalLayout,
			initialSlide: carouselIndex,
			accessibility: false, // Disable react-slick's keyboard handling, we handle it ourselves
			arrows: false,
			draggable: true,
			swipe: true,
			swipeToSlide: isMobile,
			speed: REACT_SLICK_SPEED,
			onReInit: () => {
				if (isReinitSlider.current) {
					centerThumbnailInView({
						enableCenter: true,
						containerRef,
						carouselIndex,
						isHorizontalLayout,
						isMobile,
						setThumbnailActivePos,
						setRightTranslateX
					});
					setTimeout(() => {
						isReinitSlider.current = false;
					}, ANIMATION_SPEED);
				} else if (touchMove !== 0) {
					const slickTrack = getSlickTrack();
					if (!slickTrack) return;

					// Bring the active thumbnail back to the last position before touch move
					let transform = `translate3d(${thumbnailActivePos}px, 0, 0)`;
					slickTrack.style.setProperty('transform', transform);

					// Move the thumbnail according to touch move
					transform = `translate3d(${
						thumbnailActivePos + touchMove
					}px, 0, 0)`;
					slickTrack.style.setProperty('transform', transform);
				}
			}
		}),
		[
			carouselIndex,
			isMobile,
			isHorizontalLayout,
			touchMove,
			thumbnailActivePos
		]
	);
	const mergedSettings = {
		...settings,
		...(slidesToShow && { slidesToShow })
	};

	const getSlideToDisplay = () => {
		if (!containerRef.current) {
			return SLIDE_TO_SHOW;
		}

		const thumbWrapperHeight = document.querySelector(
			'#gallery-modal-thumbnail-wrapper'
		).clientHeight;
		const slideHeight =
			containerRef.current.querySelector('.slick-slide').clientHeight;

		const _slidesToShow = thumbWrapperHeight / slideHeight;

		return _slidesToShow > 3
			? Math.floor(_slidesToShow)
			: Math.round(_slidesToShow);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const updateSlidesToShow = useCallback(
		window._.debounce(() => {
			const calculatedSlidesToShow = getSlideToDisplay();
			const limitedSlidesToShow = Math.min(
				calculatedSlidesToShow,
				SLIDE_TO_SHOW,
				imagesCount
			);

			setSlidesToShow(limitedSlidesToShow);
		}, 100),
		[]
	);

	useEffect(() => {
		// sync with carousel
		if (isInitialMount.current) {
			isInitialMount.current = false;
		} else {
			// When navigation came from VoiceOver focus (onFocusSlide), skip animation
			// to prevent DOM shift that confuses VoiceOver into jumping +1 extra.
			const dontAnimate = focusTriggeredNav.current;
			focusTriggeredNav.current = false;
			slider.current.slickGoTo(carouselIndex, dontAnimate);
			centerThumbnailInView({
				enableCenter: true,
				containerRef,
				carouselIndex,
				isHorizontalLayout,
				isMobile,
				setThumbnailActivePos,
				setRightTranslateX
			});
		}
	}, [carouselIndex, isMobile, isHorizontalLayout]);

	useEffect(() => {
		if (!containerRef.current) return;

		const wrappers =
			containerRef.current.querySelectorAll('button[role="tab"]');
		wrappers.forEach((wrapper) => {
			if (isAddedTabIndex && wrapper.getAttribute('tabindex') !== '0') {
				wrapper.setAttribute('tabindex', '0');
			}
			if (!isAddedTabIndex && wrapper.getAttribute('tabindex') !== '-1') {
				wrapper.setAttribute('tabindex', '-1');
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddedTabIndex, isMobile]);

	// react-slick marks off-screen slides with aria-hidden="true", which hides
	// thumbnail buttons from mobile screen readers (VoiceOver / TalkBack).
	// Use a MutationObserver to strip aria-hidden whenever react-slick sets it.
	useEffect(() => {
		const container = containerRef.current;

		const removeAriaHiddenFromSlides = () => {
			container
				?.querySelectorAll('.slick-slide[aria-hidden]')
				.forEach((slide) => slide.removeAttribute('aria-hidden'));
		};

		if (!container) {
			return undefined;
		}

		removeAriaHiddenFromSlides();

		const observer = new MutationObserver(removeAriaHiddenFromSlides);
		observer.observe(container, {
			subtree: true,
			attributeFilter: ['aria-hidden']
		});

		return () => observer.disconnect();
	}, []);

	const isClickThrottled = useRef(false);
	const onClickSlide = (index) => {
		if (isClickThrottled.current) {
			return;
		}

		galleryTrackingEvents.clickThumbnail(widgetName, windowId, {
			currentItem: carouselIndex + 1,
			totalItems: imagesCount
		});
		setCarouselIndex(index);
		modalSliderRef.current.slickGoTo(index);

		requestAnimationFrame(() => {
			const buttons =
				containerRef.current.querySelectorAll('button[role="tab"]');
			buttons[index]?.focus();
		});

		isClickThrottled.current = true;
		setTimeout(() => {
			isClickThrottled.current = false;
		}, ANIMATION_SPEED);
	};

	// Navigation handler with animation lock (like react-slick's slideHandler)
	const navigateToSlide = useCallback(
		(index) => {
			// Block navigation if animation is in progress (waitForAnimate behavior)
			if (animationEndCallback.current) {
				return; // Ignore keypress during animation
			}

			setCarouselIndex(index);
			modalSliderRef.current.slickGoTo(index, true);

			const thumbnailButtons =
				containerRef.current.querySelectorAll('button[role="tab"]');
			requestAnimationFrame(() => {
				thumbnailButtons[index]?.focus({ preventScroll: true });
			}); // Create animation lock and schedule its release after animation completes
			animationEndCallback.current = setTimeout(() => {
				animationEndCallback.current = null; // Release lock
			}, ANIMATION_SPEED);
		},
		[modalSliderRef, setCarouselIndex]
	);

	const onFocusSlide = (index, event) => {
		const focusCameFromOutside = !containerRef.current?.contains(
			event?.relatedTarget
		);
		// Skip during touch gestures — dragging thumbnails should NOT
		// change the main image. VoiceOver doesn't fire touch events,
		// so VoiceOver swipe-right still triggers this correctly.
		if (isTouching.current) {
			return;
		}

		// Skip when focus enters from outside — Container onFocus
		// handles re-entry by syncing viewport to carouselIndex.
		// Without this guard, onFocusSlide fires on the WRONG button
		// before Container onFocus can redirect → index jumps.
		if (focusCameFromOutside) {
			return;
		}

		// When VoiceOver swipe / Tab focuses a thumbnail within container, navigate to it
		if (index !== carouselIndex) {
			focusTriggeredNav.current = true; // Tell useEffect to skip thumbnail animation
			setCarouselIndex(index);
			modalSliderRef.current.slickGoTo(index, true);
		}
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleKeyDown = useCallback(
		(event) => {
			// Extract key immediately before debounce to avoid React event pooling issues
			const { key } = event;

			// WAI-ARIA Authoring Practices: each arrow key must follow its natural direction
			// Vertical layout: ArrowUp (prev) / ArrowDown (next)
			// Horizontal layout (desktop horizontal pref or mobile): ArrowLeft (prev) / ArrowRight (next)
			const prevKey =
				isMobile || isHorizontalLayout ? 'ArrowLeft' : 'ArrowUp';
			const nextKey =
				isMobile || isHorizontalLayout ? 'ArrowRight' : 'ArrowDown';

			const isPrev = key === prevKey;
			const isNext = key === nextKey;

			if (!isPrev && !isNext) {
				return;
			}

			// Prevent default scrolling and stop event propagation immediately
			event.preventDefault();
			event.stopPropagation();

			let nextIndex = carouselIndex;

			if (isPrev) {
				nextIndex =
					carouselIndex > 0
						? carouselIndex - 1
						: imagesToDisplay.length - 1;
			} else if (isNext) {
				nextIndex =
					carouselIndex < imagesToDisplay.length - 1
						? carouselIndex + 1
						: 0;
			}

			// Only proceed if the index changed
			if (nextIndex !== carouselIndex) {
				navigateToSlide(nextIndex);
			}
		},
		[
			carouselIndex,
			imagesToDisplay.length,
			isMobile,
			isHorizontalLayout,
			navigateToSlide
		]
	);

	return (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div
			ref={containerRef}
			className={`slider-container thumbnails-container ${
				isMobile ? 'mobile' : ''
			} ${isMobile && carouselIndex === 0 ? 'ml-4' : ''} ${
				isHorizontalLayout ? 'horizontal-layout' : ''
			}`}
			onKeyDown={handleKeyDown}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
			onTouchMove={handleTouchMove}
			onFocus={(event) => {
				const focusCameFromOutside = !containerRef.current?.contains(
					event.relatedTarget
				);

				if (isMobile) {
					// Mobile: do NOT redirect focus — .focus() traps VoiceOver.
					// Delay viewport sync so VoiceOver can finish navigating first.
					// If VoiceOver is passing through (swipe left to leave),
					// focus will be outside by the time RAF fires → skip.
					if (focusCameFromOutside) {
						requestAnimationFrame(() => {
							const isStillInside =
								containerRef.current?.contains(
									document.activeElement
								);
							if (isStillInside) {
								// Move VoiceOver cursor to the correct thumbnail.
								// Without this, VoiceOver re-enters at thumbnail 1
								// (first focusable element) and any swipe from
								// there changes the main image unintentionally.
								const thumbnailButtons =
									containerRef.current.querySelectorAll(
										'button[role="tab"]'
									);
								thumbnailButtons[carouselIndex]?.focus({
									preventScroll: true
								});
								// Also sync visual viewport to the active thumbnail
								slider.current?.slickGoTo(carouselIndex, true);
							}
						});
					}
					return;
				}

				// Desktop: redirect focus to active thumbnail button (tablist pattern)
				if (!isAddedTabIndex || focusCameFromOutside) {
					setIsAddedTabIndex(true);
					const thumbnailButtons =
						containerRef.current.querySelectorAll(
							'button[role="tab"]'
						);
					thumbnailButtons[carouselIndex]?.focus();
				}
			}}
			onBlur={(event) => {
				if (isMobile) return; // Mobile doesn't use tabIndex management
				const focusLeavingContainer = !containerRef.current?.contains(
					event.relatedTarget
				);

				if (focusLeavingContainer && isAddedTabIndex) {
					setIsAddedTabIndex(false);
				}
			}}
			role={isMobile ? 'presentation' : 'tablist'}
			aria-label="Vehicle image thumbnails"
			tabIndex={isMobile || isAddedTabIndex ? -1 : 0}
		>
			<Slider ref={slider} {...mergedSettings}>
				{imagesToDisplay.map((image, index) => {
					return isMobile ? (
						<button
							onClick={() => onClickSlide(index)}
							onFocus={(e) => onFocusSlide(index, e)}
							className="btn-unstyled"
							type="button"
							key={image.src}
							role="tab"
							aria-selected={index === carouselIndex}
							aria-label={`Thumbnail ${
								index + 1
							} of ${imagesCount}`}
						>
							<div className="thumbnail-img-wrapper">
								<Image
									alt={`${
										index + 1
									} per ${imagesCount} vehicle thumbnail images`}
									key={`thumb-${image.thumbnail}`}
									className={`slider-thumbnail ${
										index === carouselIndex ? 'active' : ''
									} slider-thumbnail--mobile mr-4`}
									src={image.thumbnail}
									onLoad={updateSlidesToShow}
									title={imgTitle}
								/>
							</div>
						</button>
					) : (
						<button
							onClick={() => onClickSlide(index)}
							onFocus={(e) => onFocusSlide(index, e)}
							type="button"
							className={
								isHorizontalLayout
									? 'btn-unstyled'
									: 'btn-unstyled mb-sm-4'
							}
							key={image.src}
							role="tab"
							aria-selected={index === carouselIndex}
							aria-label={`Thumbnail ${
								index + 1
							} of ${imagesCount}`}
						>
							<div className="thumbnail-img-wrapper">
								<Image
									alt={`${
										index + 1
									} per ${imagesCount} vehicle thumbnail images`}
									key={`thumb-${image.thumbnail}`}
									className={`slider-thumbnail w-100 ${
										index === carouselIndex ? 'active' : ''
									} slider-thumbnail--${
										isHorizontalLayout
											? 'horizontal'
											: 'desktop'
									}`}
									src={image.thumbnail}
									onLoad={updateSlidesToShow}
									title={imgTitle}
								/>
							</div>
						</button>
					);
				})}
			</Slider>
		</div>
	);
}

Thumbnails.propTypes = {
	isMobile: PropTypes.bool,
	carouselIndex: PropTypes.number.isRequired,
	setCarouselIndex: PropTypes.func.isRequired,
	modalSliderRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.instanceOf(Element) })
	]),
	imgTitle: PropTypes.string,
	isHorizontalLayout: PropTypes.bool
};

export default Thumbnails;
